import React, { useState, useEffect } from 'react';
import Barcode from 'react-barcode';
import BarcodeScanner from './components/BarcodeScanner';
import { generatePass } from './services/api';
import './App.css';

function App() {
    const [isCameraSupported, setIsCameraSupported] = useState(true);
    const [barcodeData, setBarcodeData] = useState('');
    const [passUrl, setPassUrl] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isScanning, setIsScanning] = useState(false);

    useEffect(() => {
        // This function checks for available video devices (cameras).
        const checkCameraSupport = async () => {
            // First, check if the browser supports mediaDevices at all.
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                console.log("mediaDevices API not supported by this browser.");
                setIsCameraSupported(false);
                return;
            }

            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                // Check if any of the enumerated devices is a video input.
                const hasCamera = devices.some(device => device.kind === 'videoinput');
                setIsCameraSupported(hasCamera);

                if (!hasCamera) {
                    console.log("No camera found on this device.");
                }
            } catch (err) {
                console.error("Error enumerating devices:", err);
                setIsCameraSupported(false);
            }
        };

        checkCameraSupport();
    }, []);

    const handleScan = (result) => {
        if (result) {
            setBarcodeData(result.getText());
            setIsScanning(false);
        }
    };

    const handleGenerateClick = async () => {
        if (!barcodeData) {
            setError('Please enter or scan a barcode first.');
            return;
        }
        // Reset state before making a new request
        setIsLoading(true);
        setError('');
        setPassUrl('');

        try {
            // Call the API function from services/api.js
            const response = await generatePass(barcodeData);
            // The backend should return a JSON object like { passUrl: '...' }
            setPassUrl(response.data.passUrl);
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Failed to generate pass. Please check the console and try again.';
            setError(errorMessage);
            console.error(err);
        } finally {
            // This will run whether the request succeeds or fails
            setIsLoading(false);
        }
    };

    return (
        <div className="App">
            <header className="App-header">
                <h1>THW Ausweis zu Google Wallet</h1>
            </header>

            {/* Display the "Add to Google Wallet" button when the URL is available */}
            {passUrl && (
                <div className="pass-container">
                    <h3>Wallet Pass erfolgreich erstellt!</h3>
                    <p>Ein neuer Pass wurde erstellt für <pre>{barcodeData}</pre></p>
                    <p>Klicke auf den Button um es deinem Google Wallet hinzuzufügen.</p>
                    <a href={passUrl} target="_blank" rel="noopener noreferrer">
                        <img
                            src={process.env.PUBLIC_URL + "de_add_to_google_wallet_wallet-button.svg"}
                            alt="Add to Google Wallet"
                            style={{ marginTop: '1rem' }}
                        />
                    </a>
                </div>
            )}

            <main className="App-content">
                {isScanning && isCameraSupported ? (
                    <>
                        <BarcodeScanner onScan={handleScan} />
                        <button onClick={() => setIsScanning(false)} className="button button-secondary" style={{ marginTop: '1rem' }}>
                            Abbrechen
                        </button>
                    </>
                ) : (
                    <div className="input-container">
                        {isCameraSupported ? (
                            <p>THW-Ausweis hier einscannen oder manuell eintragen.</p>
                        ) : (
                            <p>Es wurde keine Kamera gefunden. Bitte den Barcode manuell eingeben.</p>
                        )}
                        {barcodeData && (
                            <div className="scan-result">
                                <h3>Barcode erkannt</h3>
                                <div className="generated-barcode-container">
                                    <Barcode
                                        value={barcodeData}
                                        format="CODE128"
                                        lineColor="#000"
                                        background="#ffffff"
                                        height={40}
                                        displayValue={true}
                                    />
                                </div>
                            </div>
                        )}

                        <input
                            type="text"
                            value={barcodeData}
                            onChange={(e) => {
                                setBarcodeData(e.target.value);
                            }}
                            placeholder="Barcode Daten"
                            className="barcode-input"
                        />
                        {isCameraSupported && (
                            <button onClick={() => setIsScanning(true)} className="button button-secondary">
                                {barcodeData ? 'Erneut scannen' : 'Barcode scannen'}
                            </button>
                        )}
                    </div>
                )}

                <button
                    onClick={handleGenerateClick}
                    disabled={isLoading || !barcodeData}
                    className="button button-primary"
                >
                    {isLoading ? 'Verarbeitung...' : 'Wallet Pass erstellen'}
                </button>

                {error && <p className="error">{error}</p>}
            </main>
            <footer className="disclaimer-box">
                <p>
                    <strong>Haftungsausschluss: </strong>
                    Dieses Tool erstellt einen Wallet-Pass basierend auf den von bereitgestellten Barcode-Daten.
                    Es liegt in Verantwortung des Nutzers zu überprüfen, ob der erstellte Pass korrekt ist und ordnungsgemäß funktioniert.
                    Dieser Dienst wird ohne jegliche Gewährleistung zur Verfügung gestellt, und der Nutzer übernimmt die
                    volle Verantwortung für dessen Verwendung. Dies ist keine offizielle THW Anwendung.<br/><br/>
                    Weitere Informationen gibt es <a href={"https://gitlab.com/thw-wallet"}>hier</a>.
                </p>
            </footer>
        </div>
    );
}

export default App;
